<template>
  <v-navigation-drawer
    v-model="showNavBarComputed"
    class="side-bar"
    :width="$vuetify?.display?.xs ? '1000' : '270'"
  >
    <div>
      <v-text-field
        v-model="search"
        :label="t('search')"
        variant="outlined"
        density="compact"
        hide-details
        prepend-inner-icon="mdi-magnify"
        clearable
        class="ma-2"
        autocomplete="off"
      />
      <v-divider></v-divider>
    </div>
    <div class="side-items">
      <v-list density="compact" nav :opened="opened">
        <SideBarList
          v-if="filteredFavorites?.children?.length"
          :item="filteredFavorites"
          :search="search"
          style="--prepend-width: 16px"
          isFavorites
        />
        <SideBarList
          v-for="route in filteredNav"
          :key="`${route.path} ${route.name}`"
          :item="route"
          :search="search"
          style="--prepend-width: 16px"
        />
        <div ref="bottomList" v-if="!search"></div>
      </v-list>
      <div
        v-if="!filteredFavorites && !filteredNav.length"
        class="nothing-here"
      >
        {{ t('nothing-here') }}
        <v-icon>mdi-robot-happy-outline</v-icon>
      </div>
    </div>
    <div ref="bottomNav"></div>
    <Teleport v-if="teleportBottom" :to="teleportBottom">
      <v-divider></v-divider>
      <v-list class="bottom-nav" density="compact" nav>
        <PremiumWizardButton />
        <Altametrics />
        <WhatsNew />
        <IWishThisAppCould />
        <SupportSide />
        <ZdChat />
      </v-list>
    </Teleport>
  </v-navigation-drawer>
</template>

<script setup>
import { computed, toRefs, ref, onBeforeMount, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';

import { shouldRedirectToV2, redirectToV2App } from '../helpers/v2Redirection';
import { retrieveTokensAndSetCookies } from '../helpers/token_extraction';
import SideBarList from './SideBarList.vue';
import SupportSide from './bottomSideBar/SupportSide.vue';
import WhatsNew from './bottomSideBar/whatsNew/WhatsNew.vue';
import ZdChat from './bottomSideBar/zenDesk/ZDChat.vue';
import IWishThisAppCould from './bottomSideBar/IWishThisAppCould.vue';
import Altametrics from './bottomSideBar/Altametrics.vue';
import usePermissions from '../composables/usePermissions';
import { useRootStore } from '../stores/root';
import { getNavItems, navItems } from '../helpers/navItems';
import useDataPassUser from './datapass/useDataPassUser';
import { getCookieDomain, getSecureCookie } from '../helpers/cookie_handler';
import Cookies from 'js-cookie';
import { baseGet } from '../helpers/api';
import { useI18n } from 'vue-i18n';
import PremiumWizardButton from './bottomSideBar/PremiumWizardButton.vue';
import { getApiTokenCIMT } from '../helpers/ebos_token.js';
const { t } = useI18n();

const props = defineProps({
  showNavBar: {
    type: Boolean,
    default: true,
  },
  routes: {
    type: Array,
    default: () => [],
  },
});
const emit = defineEmits(['update:showNavBar']);

const { showNavBar, routes } = toRefs(props);

const route = useRoute();
function addBaseToRoute(path) {
  // strip the last / from the base url, so /admin/ => /admin, /admin => /admin, / => /
  const stripped = import.meta.env.BASE_URL.replace(/\/$/, '');
  return import.meta.env.BASE_URL === '/' ? path : `${stripped}${path}`;
}
const currentRoute = computed(() => addBaseToRoute(route.path));
const showNavBarComputed = computed({
  get() {
    return showNavBar.value;
  },
  set(value) {
    emit('update:showNavBar', value);
  },
});

const store = useRootStore();
const {
  isAdmin,
  favoritePages,
  isOrgAdmin,
  enterpriseClient,
  isCrewMember,
  language,
  orgId,
  userId,
  user,
  countryCode,
} = storeToRefs(store);

onBeforeMount(async () => {
  await getNavItems();
});

const router = useRouter();
const flattenedRoutes = computed(() => {
  const reducer = (acc, route) => {
    if (route.children) {
      const children = route.children.reduce(reducer, []);
      acc.push(...children);
    } else if (route.path) {
      acc.push({
        ...route,
        menuPath: addBaseToRoute(route.path),
      });
    }
    return acc;
  };
  return routes.value.reduce(reducer, []);
});

function checkPermission(menuItem) {
  if (menuItem.hidden) return false;
  if (menuItem.LegacyCAndIRedirect) {
    menuItem = getLegacyCIRedirectMenuItem(menuItem);
  }
  const { hasPermission } = usePermissions(
    menuItem.permissions,
    menuItem.checkEveryPermission,
  );
  const { orgHasApp } = usePermissions(menuItem.appAccess);
  const crewItemWithPermission =
    isCrewMember.value &&
    menuItem.crewMenu &&
    hasPermission.value &&
    orgHasApp.value;
  if (crewItemWithPermission) return menuItem;
  else if (isCrewMember.value) return false;
  if (!isCrewMember.value && menuItem.crewMenu) return false;
  if (menuItem.test) {
    // only QSRSoft admins can see test menuItems
    if (isAdmin.value) return menuItem;
    return false;
  }
  if (menuItem.adminOnly) {
    if (isAdmin.value || isOrgAdmin.value) {
      if (orgHasApp.value) {
        return menuItem;
      }
    }
    return false;
  }
  if (menuItem.alwaysShowForQsrAdmins) {
    if (isAdmin.value) return menuItem;
  }
  if (menuItem.appAccess === 'datapass') {
    return {
      ...menuItem,
      showItem: useDataPassUser(menuItem),
    };
  }
  if (menuItem.LegacyCAndIRedirect) {
    return menuItem;
  }

  if (menuItem.id === 'messaging') {
    const { orgHasApp } = usePermissions('disable_messaging');
    if (orgHasApp.value) return false;
  }

  if (orgHasApp.value) {
    if (hasPermission.value) return menuItem;
  }
  return false;
}
const allowedNav = computed(() => {
  function reducer(parent) {
    return function innerReduce(acc, menuItem) {
      let name = menuItem.name;
      if (language.value?.toLowerCase() !== 'en') {
        name = menuItem[`name${language.value.toUpperCase()}`] || menuItem.name;
      }
      const newMenuItem = {
        enterpriseClients:
          menuItem.enterpriseClients || parent.enterpriseClients,
        permissions: menuItem.permissions || parent.permissions,
        appAccess: menuItem.appAccess || parent.appAccess,
        adminOnly: menuItem.adminOnly || parent.adminOnly,
        test: menuItem.test || parent.test,
        hidden: menuItem.hidden || parent.hidden,
        countryCode: menuItem.countryCode || parent.countryCode,
        name,
        ...menuItem,
      };
      if (menuItem.children) {
        const children = menuItem.children.reduce(reducer(newMenuItem), []);
        if (children.length) {
          acc.push({
            ...newMenuItem,
            name,
            children,
          });
        }
      } else if (newMenuItem.path) {
        if (
          newMenuItem.enterpriseClients &&
          !newMenuItem.enterpriseClients.includes(enterpriseClient.value)
        ) {
          return acc;
        }
        if (
          newMenuItem.countryCode &&
          !newMenuItem.countryCode.includes(countryCode.value)
        ) {
          return acc;
        }

        const route = flattenedRoutes.value.find(
          (route) => route.menuPath === newMenuItem.path,
        );
        const menuItemRoute = {
          ...route,
          ...newMenuItem,
          path: route?.path || newMenuItem?.path,
          onClick: () => {
            if (route) {
              router.push(route.path);
            } else {
              if (shouldRedirectToV2(newMenuItem.path).shouldRedirect) {
                retrieveTokensAndSetCookies().then(() => {
                  redirectToV2App(newMenuItem.path);
                });
                // redirectToV2App(newMenuItem.path);
              } else {
                window.location.href = newMenuItem.path;
              }
            }
          },
          name,
        };
        const newRoute = checkPermission(menuItemRoute);
        if (newRoute) acc.push(newRoute);
      }
      return acc;
    };
  }
  return navItems.value.reduce(reducer({}), []);
});

const search = ref();

function filterReducer(acc, route) {
  const routeNameIsInSearch = route.name
    ?.toLowerCase()
    .includes(search.value.toLowerCase());
  const tagsAreInSearch = route.tags?.some((tag) =>
    tag?.toLowerCase().includes(search.value.toLowerCase()),
  );
  if (routeNameIsInSearch || tagsAreInSearch) {
    acc.push(route);
  } else if (route.children) {
    const children = route.children.reduce(filterReducer, []);
    if (children.length) {
      acc.push({
        ...route,
        children,
      });
    }
  }
  return acc;
}

const filteredNav = computed(() => {
  if (!search.value) return allowedNav.value;
  return allowedNav.value.reduce(filterReducer, []);
});

// favorites stuff
const favorites = computed(() => {
  if (!favoritePages.value) return null;
  const flattener = (acc, route) => {
    if (route.children) {
      const children = route.children.reduce(flattener, []);
      acc.push(...children);
    } else if (route.path) {
      if (favoritePages.value.includes(route.meta?.id)) {
        acc.push(route);
      }
    }
    return acc;
  };
  const favNav = allowedNav.value.reduce(flattener, []);
  favNav.sort(
    (a, b) =>
      favoritePages.value.indexOf(a.meta.id) -
      favoritePages.value.indexOf(b.meta.id),
  );
  return {
    name: t('Favorites'),
    icon: 'mdi-star',
    children: favNav,
  };
});

const filteredFavorites = computed(() => {
  if (!favorites.value) return null;
  if (!search.value) return favorites.value;
  const children = favorites.value.children.reduce(filterReducer, []);
  if (!children.length) return null;
  return {
    ...favorites.value,
    children,
  };
});

const opened = ref([]);
function getOpened() {
  const allnav = [...filteredNav.value];

  if (filteredFavorites.value) allnav.unshift(filteredFavorites.value);
  if (!allnav.length) return (opened.value = []);

  // find the current route path
  if (!search.value) {
    const findRouteNames = (navList, routeNames) => {
      for (const navItem of navList) {
        if (navItem.menuPath === currentRoute.value) {
          routeNames.push(`${navItem.name}${navItem.path}`);
          return true;
        }
        if (navItem.children) {
          if (findRouteNames(navItem.children, routeNames)) {
            routeNames.unshift(`${navItem.name}${navItem.path}`);
            return true;
          }
        }
      }
      return false;
    };
    const routeNames = [];
    findRouteNames(allnav, routeNames);
    opened.value = routeNames;
  } else {
    // if we have a search, open the first item, all the way down
    const getFirstRoute = (navItems, prev) => {
      if (navItems.children)
        return getFirstRoute(navItems.children[0], [
          ...prev,
          `${navItems.name}${navItems.path}`,
        ]);
      return [...prev, `${navItems.name}${navItems.path}`];
    };
    opened.value = getFirstRoute(allnav[0], []);
  }
}

function getLegacyCIRedirectMenuItem(menuItem) {
  return {
    ...menuItem,
    loading: false,
    async onClick() {
      this.loading = true;

      const domain = getCookieDomain();
      const secure = getSecureCookie();
      Cookies.remove('myqCandi', { domain });
      // console.log('import.meta.env.VITE_APP_URL_ENV', import.meta.env.VITE_APP_URL_ENV);
      let redirectURL =
        !import.meta.env.VITE_APP_URL_ENV?.toLowerCase() ||
        import.meta.env.VITE_APP_URL_ENV?.toLowerCase() === 'prod'
          ? 'https://ci.myqsrsoft.com/'
          : `https://${import.meta.env.VITE_APP_URL_ENV?.toLowerCase()}-ci.myqsrsoft.com/`;
      // if (domain === 'localhost') redirectURL = 'http://localhost:3000/';
      try {
        const ebosToken = await getApiTokenCIMT(orgId.value);
        let eid = userId.value || 'MissingEid';
        const eid_name = user.value?.userName || 'Sign In';
        const myqv2_email = user.value?.email || '';

        if (eid_name !== 'Sign In' && eid === 'MissingEid') {
          const nameArr = eid_name.split(' ');
          eid = [nameArr[1], nameArr[0]].join('').slice(0, 10).toLowerCase();
        }

        const myqV2CookieJarStr = JSON.stringify({
          clientId: orgId.value,
          eid_name,
          eid,
          myqv2_email,
          V2: true,
          ebosToken: ebosToken.token,
        });

        Cookies.set('myqCandi', myqV2CookieJarStr, {
          path: '/',
          domain,
          sameSite: 'Strict',
          secure,
        });

        window.open(redirectURL, '_blank').focus();
      } catch (e) {
        console.error('Error capturing redirect to candi credentials', e);
        window.open(`${redirectURL}#support`, '_blank').focus();
      } finally {
        this.loading = false;
      }
    },
  };
}

watch(currentRoute, getOpened, { immediate: true });
watch(search, getOpened);

const display = useDisplay();
const isMobile = display.smAndDown;
onBeforeMount(() => {
  showNavBarComputed.value = !isMobile.value;
});

// teleport spots for bottom side bar items
const bottomList = ref();
const bottomNav = ref();
const teleportBottom = computed(() => {
  if (display.xs.value) return bottomList.value;
  return bottomNav.value;
});
</script>
<i18n>
{
  "en": {
    "search": "Search",
    "nothing-here": "Nothing Here",
    "Favorites": "Favorites",
  },
  "es": {
    "search": "Buscar...",
    "nothing-here": "Sin Resultados",
    "Favorites": "Favoritos",
  }
}
</i18n>
<style scoped lang="scss">
.side-items {
  flex-grow: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: gray;

    &:hover {
      background-color: lightgray;
    }
  }
}

.side-bar {
  overflow: hidden;
  color: rgb(var(--v-theme-content-low));
  --list-indent-size: 8px;

  :deep(.v-navigation-drawer__content) {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  :deep(.v-list-nav) {
    --list-indent-size: 12px;
  }
  :deep(.v-list-item__prepend > .v-icon) {
    opacity: 1;

    ~ .v-list-item__spacer {
      width: 16px;
    }
  }

  :deep(.v-list-item-title) {
    font-size: 15px;
    user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }
}

.nothing-here {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.bottom-nav {
  flex-shrink: 0;
}
</style>
